import React from 'react';
import { css } from 'emotion';
import {
  Modal,
  PrimaryButton,
  SpinningLoader,
  Text,
  useModalControl,
} from '@weave/design-system';
import { theme } from '@weave/theme';

interface SyncEngineActionsProps {
  sourceId?: string;
  targetName: string;
  isPinging: boolean;
  isRestarting: boolean;
  onPing: () => void;
  onRestart: () => void;
}

export const SyncEngineActions = ({
  sourceId,
  targetName,
  isPinging,
  isRestarting,
  onPing,
  onRestart,
}: SyncEngineActionsProps) => {
  const restartModalControl = useModalControl();
  const canRun = !!sourceId;

  const handleRestartClick = () => restartModalControl.openModal();

  const confirmRestart = () => {
    restartModalControl.closeModal();
    onRestart();
  };

  const pingBtnClassName = `${fixedWidthBtn} ${isPinging ? loadingBtn : ''}`;
  const restartBtnClassName = `${fixedWidthBtn} ${
    isRestarting ? loadingBtnDestructive : ''
  }`;

  return (
    <>
      <div className={actionButtonsContainer}>
        <PrimaryButton
          className={pingBtnClassName}
          onClick={onPing}
          onMouseDown={(e) => e.preventDefault()}
          disabled={!canRun}
          aria-busy={isPinging}
        >
          {isPinging ? (
            <span className={buttonLoaderWrapper}>
              <SpinningLoader size="xs" />
            </span>
          ) : (
            `Ping ${targetName}`
          )}
        </PrimaryButton>
        <PrimaryButton
          className={restartBtnClassName}
          destructive
          onClick={handleRestartClick}
          onMouseDown={(e) => e.preventDefault()}
          disabled={!canRun}
          aria-busy={isRestarting}
        >
          {isRestarting ? (
            <span className={buttonLoaderWrapper}>
              <SpinningLoader size="xs" />
            </span>
          ) : (
            `Restart ${targetName}`
          )}
        </PrimaryButton>
      </div>

      <Modal
        {...restartModalControl.modalProps}
        maxWidth={500}
        disableCloseOnEscape
        disableCloseOnOverlayClick
      >
        <Modal.Header>{`Restart ${targetName}?`}</Modal.Header>
        <Modal.Body>
          <Text>
            {`Restarting this ${targetName.toLowerCase()} will cause a brief service interruption. Are you sure you want to continue?`}
          </Text>
        </Modal.Body>
        <Modal.Actions
          destructive
          primaryLabel="Restart"
          onPrimaryClick={confirmRestart}
          secondaryLabel="Cancel"
          onSecondaryClick={restartModalControl.closeModal}
        />
      </Modal>
    </>
  );
};

const actionButtonsContainer = css`
  margin-top: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(2)};
  display: flex;
  gap: ${theme.spacing(1)};
`;

const fixedWidthBtn = css`
  min-width: 220px;
  max-width: 280px;
  padding: ${theme.spacing(1, 2)};
  white-space: nowrap;
`;

/*
 * While a command (ping / restart) is in-flight, keep the button visually in
 * its normal enabled state and suppress every ring/shadow the design system
 * draws. The design-system's `:focus-visible` box-shadow ring extends onto
 * the page background as a coloured halo; we neutralize it here across every
 * pseudo-state and additionally prevent the button from taking focus on
 * click via `onMouseDown={(e) => e.preventDefault()}`.
 */
const makeLoadingBtn = (bg: string) => css`
  pointer-events: none;
  cursor: not-allowed;
  outline: none;
  box-shadow: none;
  border: 1px solid ${bg};

  &:focus,
  &:focus-visible,
  &:focus-within,
  &:active,
  &:hover {
    outline: none;
    box-shadow: none;
    border: 1px solid ${bg};
    background: ${bg};
    background-color: ${bg};
    color: ${theme.colors.white};
  }
`;

const loadingBtn = makeLoadingBtn(theme.colors.primary50);
const loadingBtnDestructive = makeLoadingBtn(theme.colors.status.critical);

/*
 * SpinningLoader renders a <div> with 4 <div> children whose animated `border`
 * draws the spinner. Its default border color is `primary50` (blue), so on
 * the primary button it becomes invisible. Force the border color to white
 * so the spinner is visible against either button's background.
 */
const buttonLoaderWrapper = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 20px;
  line-height: 0;

  & > div {
    display: inline-block;
  }
  & > div > div {
    border-color: ${theme.colors.white} transparent transparent transparent !important;
  }
`;
